import {toBaseScalarPricingFormValues} from "@/features/pricing";
import {CallDurationOptions} from "../../../../components/offering-form/session-bundle/session/gen-info/session-duration/call-duration-form-field";
import {GetSessionApiResponse} from "../../../../react-query-hooks/session/types/edit-session";
import {EditBundleBasicFormStepFormValues} from "../schema-validation";

/**
 * The saved bundle in the edit page's form encoding.
 *
 * A tab that edits one part of the bundle, such as the checkout questions, still sends
 * the whole update, so everything else must go back exactly as saved. That includes the
 * availability schedule: the API unlinks the current one on every update and relinks
 * only the id it is sent. The form tab uses this as its starting values and the checkout
 * tab submits it with its questions on top, so the two cannot drift.
 *
 * Cover images are left out: each tab loads them separately.
 *
 * @param isPwywEnabled - the feature flag; with it off, a bundle saved as Pay What You
 *   Want reads back as Single Fee
 * @param preferredScheduleId - the form tab's fallback for a legacy bundle with no
 *   schedule of its own. The bundle's own schedule always wins.
 */
export function toSavedEditBundleFormValues(
  sessionData: GetSessionApiResponse,
  isPwywEnabled: boolean,
  preferredScheduleId?: string
): Omit<EditBundleBasicFormStepFormValues, "cover_images"> {
  const savedRedirectType =
    sessionData.redirectType ?? (sessionData.url_redirect ? "url" : "none");
  const savedPricing = toBaseScalarPricingFormValues(sessionData, isPwywEnabled);
  const isListedDuration =
    CallDurationOptions.find(
      (ele) => ele.value === sessionData.callDuration.toString()
    ) !== undefined;

  return {
    callDuration: isListedDuration
      ? sessionData.callDuration.toString()
      : !sessionData.callDuration
      ? sessionData.callDuration.toString()
      : "-1",
    callLocation: sessionData.hasGoogleMeet
      ? "google_meet"
      : sessionData.videoPlatform === "zoom"
      ? "zoom"
      : sessionData.videoLocation
      ? "video"
      : "phone",
    callPrice: savedPricing.callPrice,
    callPriceCurrency: savedPricing.callPriceCurrency,
    callPriceType: savedPricing.callPriceType,
    coverImage: sessionData.cover_picture,
    // Saved rows always carry an amount; the form's row type just does not say so.
    currencies: savedPricing.currencies.map((row) => ({
      ...row,
      callPrice: row.callPrice ?? 0,
    })),
    enable_currency_conversion: true,
    selected_currencies: sessionData.currencies
      .filter((x) => x.id)
      .filter((c) => c.is_auto_converted)
      .map((c) => c.currency.toUpperCase()),
    customCallDuration: isListedDuration ? undefined : sessionData.callDuration,
    description: sessionData.description,
    hideFromProfile: sessionData.hideServiceFromMenu,
    isPaused: sessionData.statusName === "Paused",
    isUrlRedirect: !!sessionData.url_redirect,
    redirectAfterPurchase: savedRedirectType === "url" ? "custom-url" : savedRedirectType,
    telegramGroupId: sessionData.telegramGroupChatId ?? undefined,
    name: sessionData.name,
    requestCustomerPhoneNumber: sessionData.request_phone_number,
    slug: sessionData.slug,
    url_redirect: sessionData.url_redirect,
    bookingInterval: sessionData.is_bundle ? sessionData.booking_interval : "1day",
    numberOfSessions: sessionData.is_bundle ? sessionData.no_of_sessions.toString() : "",
    // See toSavedEditSessionFormValues: the bundle's own schedule wins verbatim, the
    // fallback only covers legacy bundles that have none.
    availabilityScheduleId: sessionData.schedule.id || preferredScheduleId || "",
  };
}
